import { ref } from "vue"
import { Core, Identification, BirthRecords, SocialSecurity, ProfileTypes, Citizen } from "@minicity/core"
import { FormNavState, Section, useForm } from "@/composables/useForm";
import { sessionState } from "@/core/session";
import { generateRandomId, notifyError, notifyNetworkError, timestampToYYYYMMDD } from "@/core/utils";
import { apiPost } from "@/core/api";
import { submitApplication } from "./api";

// Define all pages in the form
export type Page = 
    "selectApplicationTypes"
    | "basicCitizenInfo"
    | "socialSecurity"
    | "birthPlace" | "parents" | "adoption"
    | "siblings"
    | "mailing"
    | "idType" | "supportDocUpload"
    | "votingIntent"
    | "equifaxConsent"
    | "notificationPreferences"
    | "preSubmit" | "completed";

// Group pages into sections
export const sections:Section<Page>[] = [
    {name: "Select Applications", pages: [
        "selectApplicationTypes"
    ]},
    {name: "About You", pages: [
        "basicCitizenInfo", "birthPlace"
    ]},
    {name: "Social Security and Citizenship", pages: [
        "socialSecurity"
    ]},
    {name: "Your Parents", pages: [
        "parents", "adoption"
    ]},
    {name: "Your Siblings", pages: [
        "siblings",
    ]},
    {name: "Mailing Address", pages: [
        "mailing",
    ]},
    {name: "Supporting Documentation / ID", pages: [
        "idType", "supportDocUpload"
    ]},
    {name: "Final Questions", pages: [
        "votingIntent", "equifaxConsent", "notificationPreferences"
    ]},
    {name: "Submit", pages: [
        "preSubmit"
    ]},
];

export interface ModuleState {
    application: Partial<Identification.FormData>,
    supportDocuments: Core.Document[],
    notificationPreferences: Citizen.NotificationPreferences,

    formNavState: FormNavState<Page>,
    miscState: any,
}

export const moduleState = ref<ModuleState|null>(null);

export const initNewApplication = (initialMiscState: any):ModuleState => {
    return {
        application: initFormData(),
        supportDocuments: [],
        notificationPreferences: sessionState.session!.user.preferences.notifications,

        formNavState: {
            activePage: "selectApplicationTypes",
            previousPages: [],
            errors: [],
        },

        miscState: initialMiscState
    };
};

export const initFormData = ():Partial<Identification.FormData> => {
    // Pre-populate any fields we can from the citizen's profile, as well as any other defaults
    const profile = sessionState.session!.user.profile;
    return {
        firstName: profile.firstName,
        middleName: profile.middleName,
        lastName: profile.lastName,
        dateOfBirth: timestampToYYYYMMDD(profile.dateOfBirth),
    };
}

export const initBirthRecordFormData = ():Partial<BirthRecords.FormData> => {
    // Pre-populate any fields we can from the citizen's profile, as well as any other defaults
    return {
        motherBirthState: ProfileTypes.State.Unknown,
        fatherBirthState: ProfileTypes.State.Unknown,
    };
};

export const initSocialSecurityFormData = ():Partial<SocialSecurity.FormData> => {
    // Pre-populate any fields we can from the citizen's profile, as well as any other defaults
    const profile = sessionState.session?.user.profile;
    return profile ? {
        sex: profile.sex,
        previousSocialSecurityNumber: profile.socialSecurityNumber,
    } : {};
};

export const submitModule = async (draftApplicationId: string) => {  
    // Collate all the required info for the application, and submit it to the server
    return submitApplication({
        citizenId: sessionState.session!.user.id,
        formData: moduleState.value!.application! as Identification.FormData,
        supportDocuments: moduleState.value!.supportDocuments,
        notificationPreferences: moduleState.value!.notificationPreferences,
        idempotencyToken: draftApplicationId,
        draftApplicationId: draftApplicationId,
    });
};

/*
 * Types used in the form
 */
export const districts: Partial<{[key in ProfileTypes.StateNotUnknown]: string[]}> = {
    "NYC": ["BRONX", "QUEENS", "MANHATTAN", "STATEN_ISLAND", "BROOKLYN"],
    "VI": ["STCROIX", "STTHOMAS"],
}

export const siblingStates: (ProfileTypes.StateNotUnknown)[] = [
    ProfileTypes.StateNotUnknown["FL - Florida"]
];

export enum IdTypeOptions {
    "Primary ID" = "PRIMARY",
    "Secondary ID" = "SECONDARY",
    "I don't have any ID" = "NONE"
}

export enum EquifaxConsentOptions {
    "Yes, my data can be shared" = "YES",
    "No, do not share my data" = "NO"
}

export enum YesNo {
    "Yes" = "YES",
    "No" = "NO",
}